// Data source for the Plan Comparison table
// (see src/components/V2/Sections/PlanComparisonTable).
//
// Values transcribed from the 2025 pricing page design. Column order is always
// [Core, Plus, Pro].

// ---- Feature rows (tick / dash / text / rate+note / "coming soon" badge) ---

export type FeatureCellValue =
  | boolean
  | string
  | {
      rate: string
      note?: string
    }
  | { badge: string }

export type FeatureRow = {
  feature: string
  description?: string
  values: [FeatureCellValue, FeatureCellValue, FeatureCellValue]
}

// ---- Groups ---------------------------------------------------------------

export type FeatureGroup = {
  title: string
  subtitle?: string
  rows: FeatureRow[]
}

export type PlanComparisonGroup = FeatureGroup

// ---- Columns --------------------------------------------------------------

export type PlanColumn = {
  name: string
  highlighted?: boolean
}

export const PLAN_COMPARISON_FEATURE_LABEL = "Plans Features"

export const PLAN_COMPARISON_COLUMNS: readonly [
  PlanColumn,
  PlanColumn,
  PlanColumn,
] = [
  { name: "Core" },
  { name: "Plus", highlighted: true },
  { name: "Pro" },
] as const

// ---- Summary (always visible) ---------------------------------------------

export const PLAN_COMPARISON_SUMMARY_ROWS: FeatureRow[] = [
  { feature: "Yoco POS", values: [true, true, true] },
  { feature: "Tap to Pay on iPhone", values: [true, true, true] },
  { feature: "Loyalty", values: [false, true, true] },
  { feature: "Multi-location management", values: [false, true, true] },
  { feature: "Appointment Management", values: [false, true, true] },
  { feature: "Free Fast Payouts", values: [false, false, true] },
  {
    feature: "Table management with table-side ordering",
    values: [false, false, true],
  },
  { feature: "Advanced Accounting Integrations", values: [false, false, true] },
  {
    feature: "Monthly subscription fee",
    values: ["R0", "R249/month", "R499/month"],
  },
]

// ---- Full breakdown (hidden until toggled) --------------------------------

const PER_TRANSACTION_R2 = "+ R2.00 Per Transaction"
const PER_TRANSACTION_R5 = "+ R5.00 Per Transaction"

const rateRow = (
  feature: string,
  description: string,
  [core, plus, pro]: [string, string, string],
  note: string
): FeatureRow => ({
  feature,
  description,
  values: [
    { rate: core, note },
    { rate: plus, note },
    { rate: pro, note },
  ],
})

const customRow = (feature: string): FeatureRow => ({
  feature,
  values: ["Custom", "Custom", "Custom"],
})

export const PLAN_COMPARISON_BREAKDOWN: PlanComparisonGroup[] = [
  {
    title: "In-person",
    subtitle: "Debit drops sharply as your in-person volume grows.",
    rows: [
      {
        feature: "Up to R50k",
        description: "Debit",
        values: ["2.30%", "2.10%", "1.95%"],
      },
      {
        feature: "Up to R50k",
        description: "Credit",
        values: ["2.30%", "2.20%", "2.15%"],
      },
      {
        feature: "Up to R50k",
        description: "Intl.",
        values: ["3.20%", "3.10%", "3.05%"],
      },
      {
        feature: "Up to R50k",
        description: "Amex",
        values: ["3.40%", "3.30%", "3.25%"],
      },
      {
        feature: "R50k – R200k",
        description: "Debit",
        values: ["1.35%", "1.25%", "1.20%"],
      },
      {
        feature: "R50k – R200k",
        description: "Credit",
        values: ["2.30%", "2.20%", "2.15%"],
      },
      {
        feature: "R50k – R200k",
        description: "Intl.",
        values: ["2.75%", "2.65%", "2.60%"],
      },
      {
        feature: "R50k – R200k",
        description: "Amex",
        values: ["3.00%", "2.90%", "2.85%"],
      },
      customRow("R200k+"),
    ],
  },
  {
    title: "Online",
    subtitle: "transaction rates, ex VAT",
    rows: [
      rateRow(
        "Up to R50k",
        "Debit",
        ["2.95%", "2.85%", "2.80%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "Up to R50k",
        "Credit",
        ["2.95%", "2.85%", "2.80%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "Up to R50k",
        "Intl.",
        ["3.50%", "3.40%", "3.35%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "Up to R50k",
        "Amex",
        ["3.50%", "3.40%", "3.35%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "R50k – R200k",
        "Debit",
        ["2.75%", "2.65%", "2.60%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "R50k – R200k",
        "Credit",
        ["2.75%", "2.65%", "2.60%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "R50k – R200k",
        "Intl.",
        ["3.50%", "3.40%", "3.35%"],
        PER_TRANSACTION_R2
      ),
      rateRow(
        "R50k – R200k",
        "Amex",
        ["3.50%", "3.40%", "3.35%"],
        PER_TRANSACTION_R2
      ),
      customRow("R200k+"),
    ],
  },
  {
    title: "BNPL",
    subtitle: "(PayFlex / Happy Pay)",
    rows: [
      rateRow(
        "Up to R50k",
        "BNPL",
        ["5.25%", "5.25%", "5.25%"],
        PER_TRANSACTION_R5
      ),
      rateRow(
        "R50k – R200k",
        "BNPL",
        ["5.25%", "5.25%", "5.25%"],
        PER_TRANSACTION_R5
      ),
      customRow("R200k+"),
    ],
  },
  {
    title: "Accept payments",
    rows: [
      {
        feature: "All major cards",
        description: "Visa, Mastercard, Amex, Apple Pay, Google Pay",
        values: [true, true, true],
      },
      {
        feature: "BNPL",
        description: "Happy Pay, PayFlex",
        values: [true, true, true],
      },
    ],
  },
  {
    title: "In-person payments",
    rows: [
      {
        feature: "<1.5s transaction speeds, unlimited connectivity",
        values: [true, true, true],
      },
      { feature: "Tap to Pay on iPhone", values: [true, true, true] },
      {
        feature: "BNPL via Yoco QR",
        description: "Happy Pay, PayFlex",
        values: [true, true, true],
      },
    ],
  },
  {
    title: "Online payments",
    rows: [
      {
        feature: "E-commerce plug-ins",
        description: "Shopify, WooCommerce, Wix, Shopstar",
        values: [true, true, true],
      },
      { feature: "Payment link", values: [true, true, true] },
      { feature: "Payment page", values: [true, true, true] },
      {
        feature: "Invoices",
        description: "Unlimited",
        values: [true, true, true],
      },
    ],
  },
  {
    title: "Payouts",
    rows: [
      {
        feature: "Payouts to any South African bank",
        values: [true, true, true],
      },
      {
        feature: "Standard daily & weekly payouts",
        description: "Business day",
        values: ["Free", "Free", "Free"],
      },
      {
        feature: "Fast payouts",
        description: "Next morning, 7/7",
        values: ["0.75%", "0.50%", "Free"],
      },
      { feature: "Instant payouts", values: ["1.50%", "1.25%", "1.00%"] },
    ],
  },
  {
    title: "Risk management",
    rows: [{ feature: "Fraud protection 24/7", values: [true, true, true] }],
  },
  {
    title: "Point of sale",
    rows: [
      {
        feature: "Product catalogue, categories & modifiers",
        values: [true, true, true],
      },
      { feature: "Add Barcode Scanner anytime", values: [true, true, true] },
      {
        feature: "Basic stock tracking & alerts",
        values: [true, true, true],
      },
      {
        feature: "Stock control",
        values: [false, false, true],
      },
      {
        feature: "Bills, receipts and order slips",
        values: [true, true, true],
      },
      { feature: "Refund management", values: [true, true, true] },
      {
        feature: "Staff management, roles & permissions",
        values: [true, true, true],
      },
      {
        feature: "Staff auto-lock",
        values: [false, false, true],
      },
      { feature: "Advanced staff permissions", values: [false, false, true] },
      { feature: "Multi-location management", values: [false, true, true] },
      {
        feature: "3rd-party POS",
        description: "Lightspeed, Loyverse",
        values: [true, true, true],
      },
      {
        feature: "Prep Stations",
        description: "Save 5-10 min/order",
        values: [false, false, true],
      },
    ],
  },
  {
    title: "Marketing",
    rows: [
      { feature: "Custom receipts", values: [false, true, true] },
      {
        feature: "Custom screensaver on Neo Touch",
        values: [true, true, true],
      },
      { feature: "Yoco Loyalty", values: [false, true, true] },
    ],
  },
  {
    title: "Financial services",
    rows: [
      { feature: "Yoco Capital", values: [true, true, true] },
      { feature: "Yoco Savings", values: [true, true, true] },
      // {
      //   feature: "Debit card",
      //   values: [
      //     { badge: "Coming soon" },
      //     { badge: "Coming soon" },
      //     { badge: "Coming soon" },
      //   ],
      // },
      // {
      //   feature: "Current account",
      //   values: [
      //     { badge: "Coming soon" },
      //     { badge: "Coming soon" },
      //     { badge: "Coming soon" },
      //   ],
      // },
    ],
  },
  {
    title: "Yoco Connect marketplace",
    rows: [{ feature: "Developer Hub", values: [true, true, true] }],
  },
  {
    title: "Accounting integrations",
    rows: [
      { feature: "Stub Accounting", values: [false, true, true] },
      { feature: "Xero integration", values: [false, false, true] },
      { feature: "Sage integration", values: [false, false, true] },
      { feature: "QuickBooks integration", values: [false, false, true] },
    ],
  },
  {
    title: "POS & business software integrations",
    rows: [
      { feature: "Loyverse", values: [true, true, true] },
      { feature: "TallOrder", values: [true, true, true] },
      { feature: "Bookem", values: [true, true, true] },
      { feature: "Salonbridge", values: [true, true, true] },
    ],
  },
  {
    title: "E-commerce plug-in integrations",
    rows: [
      { feature: "Shopify", values: [true, true, true] },
      { feature: "WooCommerce", values: [true, true, true] },
      { feature: "Wix", values: [true, true, true] },
      { feature: "Shopstar", values: [true, true, true] },
    ],
  },
  {
    title: "Support & security",
    rows: [
      {
        feature: "Standard multi-channel support",
        description: "WhatsApp & chat",
        values: [true, true, true],
      },
      {
        feature: "Priority support",
        description: "Skip the queue",
        values: [false, true, true],
      },
      {
        feature: "Dedicated account manager",
        description: "R200k+ / month",
        values: [false, false, true],
      },
      { feature: "Real-time fraud detection 24/7", values: [true, true, true] },
      { feature: "30-day free trial", values: [false, true, true] },
    ],
  },
]
